import { useSession } from "app/authv2/use-session"
import { useRouter } from "next/router"
import { getAntiCSRFToken } from "@blitzjs/auth"

interface ErrorReportData {
  name: string
  message: string
  stack?: string
  userId?: string
  userEmail?: string
  organizationId?: number
  organizationName?: string
  url: string
  pathname: string
  userAgent?: string
  timestamp: string
  environment?: string
  additionalContext?: Record<string, unknown>
}

export const reportError = async (error: Error, additionalContext?: Record<string, unknown>) => {
  try {
    const errorData: ErrorReportData = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      url: window.location.href,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      additionalContext,
    }

    // Try authenticated endpoint first
    try {
      const antiCsrfToken = getAntiCSRFToken()
      const response = await fetch("/dash-api/v1/ops/error", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "anti-csrf": antiCsrfToken || "",
        },
        body: JSON.stringify(errorData),
      })

      if (response.ok) {
        return // Success with authenticated endpoint
      }

      // If 401/403, fall back to unauthenticated
      if (response.status === 401 || response.status === 403) {
        console.log("Authentication failed, falling back to unauthenticated error reporting")
      } else {
        console.error("Error reporting failed:", response.statusText)
        return
      }
    } catch (authError) {
      console.log(
        "Authenticated error reporting failed, falling back to unauthenticated:",
        authError
      )
    }

    // Fall back to unauthenticated endpoint
    const fallbackResponse = await fetch("/dash-api/v1/ops/error/unauthenticated", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(errorData),
    })

    if (!fallbackResponse.ok) {
      console.error("Unauthenticated error reporting also failed:", fallbackResponse.statusText)
    }
  } catch (reportingError) {
    // Don't let error reporting errors break the app
    console.error("Error reporting completely failed:", reportingError)
  }
}

export const useErrorReporter = () => {
  const { data: session, userId, isLoading } = useSession()
  const router = useRouter()

  const reportErrorWithContext = async (
    error: Error,
    additionalContext?: Record<string, unknown>
  ) => {
    const contextData = {
      ...additionalContext,
      userId: userId?.toString() || undefined,
      userEmail: session?.email || undefined,
      currentRoute: router.pathname,
      routeQuery: router.query,
    }

    await reportError(error, contextData)
  }

  return { reportError: reportErrorWithContext }
}
